import { Box, Button, Typography, useTheme } from '@mui/material';
import { VisibilityOutlined, ArrowBackRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { PortalView } from '../../../lib/portal-view';
import { usePortalViewStore } from '../../../stores/portal-view.store';

// ─── La banda de «Ver portal» ───────────────────────────────────────────────
// Fija y visible mientras el personal mira el portal de un alumno: de quién es
// (su código L-######), que es solo lectura y la salida a la ficha. Cuando la
// vista vence —o el servidor dice que ya no vale—, la banda lo dice y ofrece
// reabrirla desde la ficha (otra apertura, otra entrada en la auditoría).

export const PORTAL_VIEW_BANNER_HEIGHT = 40;

export function PortalViewBanner({
  view,
  onExit,
}: {
  view: PortalView;
  onExit: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const gone = usePortalViewStore((s) => s.gone);
  const who = view.studentCode ?? t('portal.view.thisStudent');

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: PORTAL_VIEW_BANNER_HEIGHT,
        zIndex: theme.zIndex.drawer + 2,
        // Ámbar con texto casi negro: contraste AA holgado.
        bgcolor: gone ? '#fca5a5' : '#f59e0b',
        color: '#0d1117',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <VisibilityOutlined fontSize="small" />
        <Typography variant="body2" fontWeight={700} noWrap>
          {gone === 'expired'
            ? t('portal.view.expired', { code: who })
            : gone === 'invalid'
              ? t('portal.view.invalidBanner', { code: who })
              : gone === 'deceased'
                ? t('portal.view.deceasedBanner', { code: who })
                : t('portal.view.banner', { code: who })}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={<ArrowBackRounded />}
        onClick={onExit}
        sx={{
          flexShrink: 0,
          bgcolor: '#0d1117',
          color: '#fff',
          '&:hover': { bgcolor: '#21262d' },
          textTransform: 'none',
        }}
      >
        {gone && gone !== 'deceased' ? t('portal.view.reopen') : t('portal.view.backToRecord')}
      </Button>
    </Box>
  );
}
